import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, TreeView } from '@/shared/ui';
import type { TreeRowAction } from '@/shared/ui';
import { Icon } from '@/shared/ui/icon';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useAgendaState } from '@/shared/navigation/react/useAgendaState';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useGetColumbusTree } from '@/api/generated/columbus-planner/columbus-planner';
import type { TransportTypeFilter } from '@/api/generated/schemas';
import { useLegacyEditForm } from '@/shared/lib/legacyMagic';
import { columbusAgenda } from './columbusAgenda';
import { columbusDetailAgenda } from './columbusDetailAgenda';
import { ColumbusTreeFilterBar } from './components/ColumbusTreeFilterBar';
import {
  COLUMBUS_NODE_TYPES,
  buildColumbusTreeData,
  expandToNode,
  parseTsNodeId,
  parseTermNodeId,
  parseDsNodeId,
  parseFullProductNodeId,
} from './columbusTreeModel';

interface ColumbusShellPageProps {
  layerId: string;
}

/** Editační tužka řádku (PHX ikonset; velikost řídí TreeView) */
function EditPencilIcon({ className }: { className?: string }) {
  return <Icon name="edit" size={11} className={className} />;
}

export function ColumbusShellPage({ layerId }: ColumbusShellPageProps) {
  const { t, i18n } = useTranslation('columbus-planner');
  const navigator = useNavigator();
  const { state } = useAgendaParams(columbusAgenda, layerId);
  const agendaState = useAgendaState(columbusAgenda, layerId);

  const transportType = state.transportType ?? null;
  const includeHistory = state.includeHistory ?? false;

  const { data, isLoading, isError, error } = useGetColumbusTree({
    ...(transportType ? { transportType } : {}),
    ...(includeHistory ? { includeHistory: true } : {}),
  });

  const { openEdit: openDsEdit } = useLegacyEditForm('DestinationSeason');

  const treeActions = useMemo<TreeRowAction[]>(
    () => [
      {
        id: 'edit-ds',
        label: t('tree.actions.edit'),
        icon: EditPencilIcon,
        appliesTo: ['destinationSeason'],
        onClick: (nodeId) => {
          const id = parseDsNodeId(nodeId);
          if (id != null) openDsEdit(id);
        },
      },
      {
        id: 'edit-product',
        label: t('tree.actions.edit'),
        icon: EditPencilIcon,
        appliesTo: ['product'],
        onClick: (nodeId) => {
          const ids = parseFullProductNodeId(nodeId);
          if (ids != null) openDsEdit(ids.dsId, { ProductID: ids.productId });
        },
      },
    ],
    [t, openDsEdit]
  );

  const treeDataBase = useMemo(
    () =>
      buildColumbusTreeData(
        data ?? { destinationSeasons: [] },
        {
          destinationSeason: t('tree.types.destinationSeason'),
          product: t('tree.types.product'),
          termsSerial: t('tree.types.termsSerial'),
          term: t('tree.types.term'),
          formatNights: (n) => t('term.nights', { count: n }),
        },
        i18n.language
      ),
    [data, t, i18n.language]
  );

  const treeData = useMemo(
    () => ({ ...treeDataBase, actionsByType: treeActions }),
    [treeDataBase, treeActions]
  );

  const defaultExpandedIds = useMemo(() => {
    if (!state.activeNodeId) return undefined;
    return [...expandToNode(treeData, state.activeNodeId, [])];
  }, [treeData, state.activeNodeId]);

  const handleNodeActivate = (nodeId: string) => {
    agendaState.patch({ activeNodeId: nodeId });
    const ts = parseTsNodeId(nodeId);
    if (ts) {
      navigator.openModal(columbusDetailAgenda, {
        productId: ts.productId,
        termsSerialId: ts.termsSerialId,
        destinationSeasonId: ts.destinationSeasonId,
      });
      return;
    }
    const term = parseTermNodeId(nodeId);
    if (term) {
      navigator.openModal(columbusDetailAgenda, {
        productId: term.productId,
        termsSerialId: term.termsSerialId,
        destinationSeasonId: term.destinationSeasonId,
        termId: term.termId,
      });
    }
  };

  const handleTransportTypeChange = (value: TransportTypeFilter | null) => {
    if (value === null) {
      agendaState.remove('transportType');
    } else {
      agendaState.patch({ transportType: value });
    }
  };

  const handleIncludeHistoryChange = (value: boolean) => {
    if (value) {
      agendaState.patch({ includeHistory: true });
    } else {
      agendaState.remove('includeHistory');
    }
  };

  return (
    <div className="bg-phx-surface flex h-full min-h-0 flex-col gap-2 p-2.5 pb-0">
      <ColumbusTreeFilterBar
        transportType={transportType}
        includeHistory={includeHistory}
        onTransportTypeChange={handleTransportTypeChange}
        onIncludeHistoryChange={handleIncludeHistoryChange}
      />
      {isLoading ? (
        <p className="text-muted-foreground text-sm">{t('states.loading')}</p>
      ) : isError ? (
        <Alert severity="error" title={t('states.errorTitle')}>
          {error instanceof Error ? error.message : t('states.errorDescription')}
        </Alert>
      ) : (
        <TreeView
          className="min-h-0 flex-1"
          data={treeData}
          activeNodeId={state.activeNodeId ?? null}
          onNodeActivate={handleNodeActivate}
          defaultExpandedIds={defaultExpandedIds}
          levelTypeIds={COLUMBUS_NODE_TYPES}
        />
      )}
    </div>
  );
}
